import { WmButton } from "@watermarkinsights/ripple-react";
import { SyntheticEvent, useEffect, useReducer, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { sendRequest } from "@/common/http";
import translate from "@/i18n/translate";
import "../Lightcast.scss";
import ApiError from "../../common/elements/ApiError";
import { handleChartArrowNavigation } from "../../common/helper";

interface PopulationByRaceEthnicity {
  changePercentage: number;
  id: number;
  race: string;
}
interface PopulationByRaceResultSet {
  currentYear: number;
  pastYear: number;
  populationByRace: PopulationByRaceEthnicity[];
}

interface PopulationByRaceEthnicityState {
  changeInPopulationRange: number[];
  changePercentages: number[] | null;
  setBarsByRace: boolean;
  populationByRaceEthnicity: PopulationByRaceEthnicity[] | null;
  pastYear: number | null;
  currentYear: number | null;
  apiError: boolean;
}

export interface PopulationByRaceEthnicityProps {
  setTooglePosition: (event: any, chartId: string) => void;
  onTabLoad: (tab: string) => void;
  onComponentLoad: (component: string) => void;
  showIntegratedHeader: boolean;
}

interface PopulationByRaceEthnicityAction {
  type: string;
  changeInPopulationRange: number[];
  populationByRaceEthnicityChangePercentages: PopulationByRaceResultSet;
  flag: boolean;
}
const defaultPopulationByRaceResultSet = {
  currentYear: 0,
  pastYear: 0,
  populationByRace: []
};

const defaultPopulationByRaceEthnicityAction = {
  type: "",
  changeInPopulationRange: [],
  populationByRaceEthnicityChangePercentages: defaultPopulationByRaceResultSet,
  flag: false
};

const reducer = (
  state: PopulationByRaceEthnicityState,
  action: PopulationByRaceEthnicityAction
) => {
  switch (action.type) {
    case "setChangeInPopulationByRaceEthnicityRange":
      return {
        ...state,
        changeInPopulationRange: action.changeInPopulationRange
      };

    case "setPopulationByRaceEthnicityChangePercentages":
      const changePercentages =
        action.populationByRaceEthnicityChangePercentages?.populationByRace.map(
          (population: PopulationByRaceEthnicity) => population.changePercentage
        );
      return {
        ...state,
        changePercentages: changePercentages
      };
    case "setBarsFlagByRace":
      return {
        ...state,
        setBarsByRace: action.flag
      };
    case "setPopulationByRaceError":
      return {
        ...state,
        pastYear: 0,
        currentYear: 0,
        populationByRaceEthnicity: [],
        changePercentages: [],
        apiError: true
      };

    case "setPopulationByRace":
      return {
        ...state,
        pastYear: action.populationByRaceEthnicityChangePercentages?.pastYear,
        currentYear:
          action.populationByRaceEthnicityChangePercentages?.currentYear,
        populationByRaceEthnicity:
          action.populationByRaceEthnicityChangePercentages?.populationByRace,
        apiError: action.populationByRaceEthnicityChangePercentages
          ? false
          : true
      };

    default:
      return state;
  }
};

export default function PopulationByRaceEthnicity({
  setTooglePosition,
  onTabLoad,
  onComponentLoad,
  showIntegratedHeader
}: PopulationByRaceEthnicityProps) {
  const initialState = {
    changeInPopulationRange: [],
    changePercentages: null,
    setBarsByRace: true,
    populationByRaceEthnicity: null,
    pastYear: null,
    currentYear: null,
    apiError: false
  };
  const [openPopoverStateByRace, setOpenPopoverStateByRace] = useState(false);

  const [selectedRowIdByRace, setSelectedRowIdByRace] = useState("");
  const [focusedBarIndex, setFocusedBarIndex] = useState<number>(-1);
  const barRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [maxTicks, setMaxTicks] = useState(Number);
  const [isMoveLeftBtnDisabledByRace, setMoveLeftBtnDisable] = useState(false);
  const [isMoveRightBtnDisabledByRace, setMoveRightBtnDisable] =
    useState(false);

  useEffect(() => {
    const applyResult = (result: PopulationByRaceResultSet) => {
      dispatch({
        ...defaultPopulationByRaceEthnicityAction,
        type: "setPopulationByRace",
        populationByRaceEthnicityChangePercentages: result
      });

      dispatch({
        ...defaultPopulationByRaceEthnicityAction,
        type: "setPopulationByRaceEthnicityChangePercentages",
        populationByRaceEthnicityChangePercentages: result
      });
      onTabLoad("raceEthinicity");
      onComponentLoad("populationByRace");
    };

    const onError = () => {
      dispatch({
        ...defaultPopulationByRaceEthnicityAction,
        type: "setPopulationByRaceError"
      });
      onTabLoad("raceEthinicity");
      onComponentLoad("populationByRace");
    };

    sendRequest(
      { uri: "/lightcast/population_by_race", camelize: true },
      applyResult,
      onError
    );
  }, []);
  const timeframe = {
    pastYear: state.pastYear,
    currentYear: state.currentYear
  };

  const getChangeInPopulationRange = (changePercentages: number[]) => {
    let hasNegativeVal = changePercentages.some((val: number) => val < 0);
    let hasPositiveVal = changePercentages.some((val: number) => val >= 0);
    let absPercentageChangeValArr = absoluteValueArray(changePercentages);

    var getIntervalRangeVal = getInterval(
      Math.max(...absPercentageChangeValArr),
      2
    );
    if (hasNegativeVal && !hasPositiveVal) {
      setMaxTicks(2);
      setBarClassList("negative");
      return [
        0,
        -getIntervalRangeVal / 2,
        -getIntervalRangeVal,
        -(getIntervalRangeVal + getIntervalRangeVal / 2),
        -getIntervalRangeVal * 2
      ];
    } else if (hasPositiveVal && !hasNegativeVal) {
      setMaxTicks(2);
      setBarClassList("positive");
      return [
        getIntervalRangeVal * 2,
        getIntervalRangeVal + getIntervalRangeVal / 2,
        getIntervalRangeVal,
        getIntervalRangeVal / 2,
        0
      ];
    } else {
      setMaxTicks(2);
      return [
        getIntervalRangeVal * 2,
        getIntervalRangeVal,
        0,
        -getIntervalRangeVal,
        -getIntervalRangeVal * 2
      ];
    }
  };

  const setBarsByRace = (changePercentages: any): JSX.Element => {
    let absPercentageChangeValArr = absoluteValueArray(changePercentages);
    var ele = document.querySelectorAll(".race-barss");

    ele.forEach(getBarsHeightVal);
    function getBarsHeightVal(item: any, index: number) {
      var n = parseFloat(changePercentages[index]) * 1;
      const getHeight = (n: number) => {
        return (
          (parseFloat(changePercentages[index]) /
            (getInterval(Math.max(...absPercentageChangeValArr), maxTicks) *
              maxTicks)) *
          100
        );
      };
      var numbersFull = getHeight(n);
      item.style.height = Math.round(Math.abs(numbersFull)) + "%";
      if (n >= 0) {
        addClassListToElement(item, "positive");
      } else {
        addClassListToElement(item, "negative");
      }
    }

    if (ele && ele.length > 0)
      dispatch({
        ...defaultPopulationByRaceEthnicityAction,
        type: "setBarsFlagByRace",
        flag: false
      });
    return <></>;
  };

  const openPopover = (
    population: PopulationByRaceEthnicity,
    event: SyntheticEvent
  ) => {
    setTooglePosition(event, "race-cohort-chart");
    setOpenPopoverStateByRace(true);
    setSelectedRowIdByRace(String(population.id));
  };

  function useClosePopoverClick(ref: any) {
    useEffect(() => {
      function handleClickOutside(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setOpenPopoverStateByRace(false);
          setFocusedBarIndex(-1);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const moveLeftBtn = useIntl().formatMessage({
    id: "app.changeInPopulation.moveLeftBtn"
  });

  const moveRightBtn = useIntl().formatMessage({
    id: "app.changeInPopulation.moveRightBtn"
  });

  const chartContainerAriaLabel = useIntl().formatMessage({
    id: "app.changeInPopulation.race.chartAriaLabel"
  });

  const addBoxShadowOnBothSides = (element: HTMLElement) => {
    element && element.classList.add("-box-shadows");
  };

  const removeBoxShadowOnBothSides = (element: HTMLElement) => {
    element && element.classList.remove("-box-shadows");
  };

  const addBoxShadowOnLeft = (element: HTMLElement) => {
    element && element.classList.add("-box-shadow-left");
  };

  const removeBoxShadowOnLeft = (element: HTMLElement) => {
    element && element.classList.remove("-box-shadow-left");
  };

  const addBoxShadowOnRight = (element: HTMLElement) => {
    element && element.classList.add("-box-shadow-right");
  };

  const removeBoxShadowOnRight = (element: HTMLElement) => {
    element && element.classList.remove("-box-shadow-right");
  };
  const setMoveLeftRightBtnDisability = (ref: any, state: string) => {
    let element = ref?.current;
    if (element?.scrollLeft === 0) {
      setMoveLeftBtnDisable(true);
      removeBoxShadowOnLeft(element);
      removeBoxShadowOnBothSides(element);
    } else {
      setMoveLeftBtnDisable(false);
      addBoxShadowOnLeft(element);
      removeBoxShadowOnBothSides(element);
    }

    if (
      element?.scrollLeft !== 0 &&
      element?.scrollLeft < element?.scrollWidth - element?.offsetWidth - 1
    ) {
      removeBoxShadowOnRight(element);
      removeBoxShadowOnLeft(element);
      addBoxShadowOnBothSides(element);
    }
    switch (state) {
      case "onload":
        if (
          element?.scrollLeft >
          element?.scrollWidth - element?.offsetWidth - 1
        ) {
          return (
            setMoveRightBtnDisable(false),
            addBoxShadowOnRight(element),
            removeBoxShadowOnBothSides(element)
          );
        } else {
          return setMoveRightBtnDisable(false), setMoveLeftBtnDisable(true);
        }

      default:
        if (
          element?.scrollLeft >
          element?.scrollWidth - element?.offsetWidth - 1
        ) {
          return setMoveRightBtnDisable(true), removeBoxShadowOnRight(element);
        } else {
          return setMoveRightBtnDisable(false), addBoxShadowOnRight(element);
        }
    }
  };
  function useSetMoveLeftRightValueByRace(ref: any) {
    useEffect(() => {
      setTimeout(() => {
        setMoveLeftRightBtnDisability(ref, "onload");
      }, 200);
    }, [ref]);
  }

  const scrollWrapperByRace = useRef(null);
  useSetMoveLeftRightValueByRace(scrollWrapperByRace);
  useClosePopoverClick(scrollWrapperByRace);

  const setChartScrollByRace = (
    element: any,
    speed: number,
    distance: number,
    direction: string
  ) => {
    var step =
      direction === "left" ? -element?.offsetWidth : element?.offsetWidth;
    let scrollAmount = 0;

    const slideTimer = setInterval(() => {
      element.scrollLeft += step;
      scrollAmount += Math.abs(step);

      if (scrollAmount >= distance) {
        clearInterval(slideTimer);
      }
      setMoveLeftRightBtnDisability(scrollWrapperByRace, "button-click");
    }, speed);
  };

  const getPopoverAmountData = (population: PopulationByRaceEthnicity) => {
    return population.changePercentage >= 0 ? "-green" : "-red";
  };

  const getNumericSignValue = (changeInPercentageVal: number) => {
    return changeInPercentageVal >= 0 ? "+" : "";
  };

  useEffect(() => {
    state.changePercentages &&
      dispatch({
        ...defaultPopulationByRaceEthnicityAction,
        type: "setChangeInPopulationByRaceEthnicityRange",
        changeInPopulationRange: getChangeInPopulationRange(
          state.changePercentages
        )
      });
  }, [state.changePercentages]);

  const handleChartContainerKeyDown = (event: React.KeyboardEvent) => {
    const totalBars = state.populationByRaceEthnicity?.length || 0;
    handleChartArrowNavigation(event, {
      totalItems: totalBars,
      currentIndex: focusedBarIndex,
      itemRefs: barRefs,
      containerRef: scrollWrapperByRace,
      onNavigate: (newIndex) => {
        setFocusedBarIndex(newIndex);
      },
      onEscape: () => {
        setOpenPopoverStateByRace(false);
        setFocusedBarIndex(-1);
      },
      onExit: () => {
        setOpenPopoverStateByRace(false);
        setFocusedBarIndex(-1);
      }
    });
  };

  return (
    <>
      {state.changePercentages && (
        <div className="population-by-race-ethnicity">
          <div className="race-cohort-chart flex-row -justified">
            {!showIntegratedHeader && (
              <div className="header">
                <div className="info" id="change-population-info">
                  <h3 className="title change-population-title">
                    {translate(
                      "app.changeInPopulation.ageCohort.changePopulationRaceEthnicity"
                    )}
                  </h3>
                  <div className="description -descrip html-desc">
                    {translate("app.changeInPopulation.helperText")}
                  </div>
                </div>
              </div>
            )}
            {!state.apiError && (
              <>
                {showIntegratedHeader && (
                  <div className="race-cohort-label">
                    {translate(
                      "app.changeInPopulation.ageCohort.changePopulationRaceEthnicity"
                    )}
                  </div>
                )}
                <div className="flex-row">
                  <WmButton
                    buttonType={"icononly"}
                    tooltip={moveLeftBtn}
                    icon={"f04d"}
                    tooltipPosition="bottom-left"
                    disabled={isMoveLeftBtnDisabledByRace}
                    onClick={() =>
                      setChartScrollByRace(
                        scrollWrapperByRace.current,
                        25,
                        100,
                        "left"
                      )
                    }
                    tabIndex={-1}
                  ></WmButton>
                  <WmButton
                    buttonType={"icononly"}
                    tooltip={moveRightBtn}
                    icon={"f054"}
                    tooltipPosition="bottom-left"
                    disabled={isMoveRightBtnDisabledByRace}
                    onClick={() =>
                      setChartScrollByRace(
                        scrollWrapperByRace.current,
                        25,
                        100,
                        "right"
                      )
                    }
                    tabIndex={-1}
                  ></WmButton>
                </div>
              </>
            )}
          </div>
          {!state.apiError && (
            <div className="flex-row -verticallycentered">
              <div className="percentage-change-label">
                {translate("app.changeInPopulation.ageCohort.percentageChange")}
              </div>
              <div
                className="race-cohort-chart"
                id="race-cohort-chart-container"
                tabIndex={0}
                role="application"
                aria-labelledby="age-cohort-header"
                ref={scrollWrapperByRace}
                aria-description={chartContainerAriaLabel}
                onKeyDown={handleChartContainerKeyDown}
              >
                <div className="race-bar-chart" id="race-bar-chart">
                  <div className="race-row bars">
                    <div className="y-axis one">
                      {state.changeInPopulationRange.map(
                        (populChange: number) => {
                          return (
                            <div className="race-segment" key={populChange}>
                              <span className="race-label">{populChange}</span>
                            </div>
                          );
                        }
                      )}
                    </div>

                    <div className="race-x-axis" id="race-x-axis">
                      {state.populationByRaceEthnicity &&
                        state.populationByRaceEthnicity.map(
                          (population: PopulationByRaceEthnicity, index: number) => {
                            var changeState =
                              population.changePercentage >= 0
                                ? translate("app.trends.increased")
                                : translate("app.trends.decreased");
                            return (
                              <div className="year wrap" key={population.id}>
                                <div className="race-col">
                                  <div
                                    className="race-barss tooltip"
                                    onFocus={(event: SyntheticEvent) =>
                                      openPopover(population, event)
                                    }
                                    ref={(el) => (barRefs.current[index] = el)}
                                    tabIndex={-1}
                                  >
                                    <div
                                      className={`population-tooltiptext tooltiptext-years ${openPopoverStateByRace &&
                                        String(population.id) ===
                                        selectedRowIdByRace
                                        ? "-visible"
                                        : "-hidden"
                                        }`}
                                    >
                                      <div className="race-tooltip-text">
                                        <div
                                          className={`population-popover-amount 
                              ${getPopoverAmountData(population)}`}
                                        >
                                          {getNumericSignValue(
                                            population.changePercentage
                                          ) +
                                            population.changePercentage +
                                            "%"}
                                        </div>
                                        <div className="population-popover-text">
                                          {translate(
                                            "app.changeInPopulation.race.popoverLabelText",
                                            {
                                              raceName: population.race,
                                              changeState: changeState,
                                              changePercentage: Math.abs(
                                                population.changePercentage
                                              ),
                                              pastYear: timeframe.pastYear,
                                              currentYear: timeframe.currentYear
                                            }
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <span className="race-label">
                                  {population.race}
                                </span>
                              </div>
                            );
                          }
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {state.setBarsByRace ? setBarsByRace(state.changePercentages) : ""}
          {!state.apiError && (
            <div className="pop-race-year">
              {translate("app.changeInPopulation.race.raceEthnicityLabel")}
            </div>
          )}
        </div>
      )}

      {state.apiError && <ApiError />}
    </>
  );
}

const addClassListToElement = (item: Element, classesToAdd: string) => {
  item.classList.add(classesToAdd);
};

const getElementListAndSetClassList = (
  elementTofetch: string,
  classToAdd: string
) => {
  var element = document.querySelectorAll(elementTofetch);
  element.forEach(setClassList);
  function setClassList(item: Element, index: number) {
    addClassListToElement(item, classToAdd);
  }
};

const getNegativeValBarView = () => {
  getElementListAndSetClassList(".race-col", "col-height");

  var barElement = document.querySelectorAll(".race-barss");
  barElement.forEach(removeBarClassList);
  function removeBarClassList(item: Element, index: number) {
    item.classList.remove("negative");
  }

  getElementListAndSetClassList(".race-label", "-label-tranform");

  getElementListAndSetClassList(".race-x-axis", "-transform");

  getElementListAndSetClassList(".race-segment", "-border-top");

  getElementListAndSetClassList(".race-tooltip-text", "-transform-tooltip");

  getElementListAndSetClassList(".race-cohort-chart", "-negative-space");
};

const getPostiveValBarView = () => {
  getElementListAndSetClassList(".race-col", "col-height");
  var barElement = document.querySelectorAll(".race-barss");
  barElement.forEach(removeBarClassList);
  function removeBarClassList(item: Element) {
    item.classList.remove("negative");
  }
};

const setBarClassList = (valStatus: string) => {
  switch (valStatus) {
    case "negative":
      return getNegativeValBarView();

    default:
      return getPostiveValBarView();
  }
};

const absoluteValueArray = (percentages: number[]) => {
  return percentages.map(Math.abs);
};

const getInterval = (largest: number, ticks: number) => {
  const minimum = largest / ticks;
  const magnitude = 10 ** Math.floor(Math.log10(minimum));
  const residual = minimum / magnitude;
  let interval;
  if (residual > 5) {
    interval = 10 * magnitude;
  } else if (residual > 3) {
    interval = 5 * magnitude;
  } else if (residual > 2) {
    interval = 3 * magnitude;
  } else if (residual > 1.5) {
    interval = 2 * magnitude;
  } else if (residual > 1) {
    interval = 1.5 * magnitude;
  } else {
    interval = magnitude;
  }
  return interval;
};
